<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { supabase } from "../lib/supabase.js";
import { useUserStore } from "../stores/user.js";

const router = useRouter();
const userStore = useUserStore();

const status = ref("Verifying your link...");
const errorMessage = ref("");

async function handleAuthCallback() {
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  try {
    let authError = null;

    // Check for error params provided by Supabase
    const earlyHashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, "")
    );
    const errorParam =
      url.searchParams.get("error") || earlyHashParams.get("error");
    const errorDesc =
      url.searchParams.get("error_description") ||
      earlyHashParams.get("error_description");
    if (errorParam || errorDesc) {
      status.value = "There was a problem verifying your sign-in link.";
      errorMessage.value = errorDesc || errorParam;
      return;
    }

    // 1) Preferred: exchange the `code` query param for a session (newer Supabase flow)
    const code = url.searchParams.get("code");
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(currentUrl);
      authError = error || null;
    } else {
      // 2) Fallback: handle tokens in the URL hash (older flow)
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, "")
      );
      const access_token = hashParams.get("access_token");
      const refresh_token = hashParams.get("refresh_token");

      if (access_token && refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });
        authError = error || null;
      } else {
        // 3) Final fallback: session may already be set by the client internally
        const { data, error } = await supabase.auth.getSession();
        if (!data?.session) {
          authError = error || new Error("No session found in callback URL.");
        }
      }
    }

    if (authError) throw authError;

    // If this is a password recovery link, send the user to the reset screen
    const typeParam =
      url.searchParams.get("type") ||
      new URLSearchParams(window.location.hash.replace(/^#/, "")).get("type");
    if (typeParam === "recovery") {
      status.value =
        "Password recovery link verified. Redirecting to reset password...";
      // Go straight to the reset password route
      setTimeout(() => router.replace("/reset-password"), 300);
      return;
    }

    // Sync the user store from the current Supabase session
    const result = await userStore.refreshFromSession();
    if (!result.success) {
      throw new Error(result.error || "Failed to refresh authenticated user.");
    }

    status.value = "Signed in successfully. Redirecting...";

    // Optional: support redirect to a specific path via ?next=/path
    const next = url.searchParams.get("next") || "/";
    setTimeout(() => router.replace(next), 400);
  } catch (e) {
    console.error("Auth callback error:", e);
    status.value = "There was a problem verifying your sign-in link.";
    errorMessage.value = e?.message || "Unknown error during authentication.";
  }
}

function goToLogin() {
  router.replace("/login");
}

onMounted(() => {
  handleAuthCallback();
});
</script>

<template>
  <div class="auth-callback">
    <div class="card">
      <h2>Authentication</h2>
      <p>{{ status }}</p>
      <p v-if="errorMessage" class="error">Error: {{ errorMessage }}</p>
      <button v-if="errorMessage" @click="goToLogin">Back to Login</button>
    </div>
  </div>
</template>

<style scoped>
.auth-callback {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: #f5f5f5;
}

.card {
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  max-width: 420px;
  width: 90%;
  text-align: center;
}

h2 {
  margin-top: 0;
}

.error {
  color: #d64545;
  margin-top: 0.75rem;
}

button {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #99b5d3;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

button:hover {
  background-color: #7d9cbb;
}
</style>
